import React from 'react';
import { ArrowRight, Plus, Minus } from 'lucide-react';

/**
 * Format a field name for display (convert snake_case to Title Case).
 */
const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};

/**
 * Format a value for display.
 */
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 italic">null</span>;
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-400 italic">empty</span>;
    }
    return value.join(', ');
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  // Handle dates
  if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
    try {
      const date = new Date(value);
      return date.toLocaleString();
    } catch {
      return value;
    }
  }

  return String(value);
};

/**
 * DiffViewer Component
 *
 * Displays field-level changes between old and new values.
 *
 * @param {Object} props
 * @param {Object} props.changes - Object with field names as keys, {old_value, new_value} as values
 * @param {boolean} props.compact - Use compact display mode
 */
const DiffViewer = ({ changes, compact = false }) => {
  if (!changes || Object.keys(changes).length === 0) {
    return (
      <div className="text-gray-500 text-sm italic">
        No changes recorded
      </div>
    );
  }

  const changeEntries = Object.entries(changes);

  if (compact) {
    return (
      <div className="space-y-1">
        {changeEntries.slice(0, 3).map(([field, change]) => (
          <div key={field} className="text-sm">
            <span className="font-medium text-gray-700">{formatFieldName(field)}</span>
            {' changed'}
          </div>
        ))}
        {changeEntries.length > 3 && (
          <div className="text-sm text-gray-500">
            +{changeEntries.length - 3} more changes
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {changeEntries.map(([field, change]) => {
        const { old_value, new_value } = change;
        const isAdded = old_value === null || old_value === undefined;
        const isRemoved = new_value === null || new_value === undefined;

        return (
          <div key={field} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
            <div className="font-medium text-gray-800 mb-2 flex items-center gap-2">
              {isAdded && <Plus className="w-4 h-4 text-green-600" />}
              {isRemoved && <Minus className="w-4 h-4 text-red-600" />}
              {formatFieldName(field)}
            </div>

            <div className="flex items-start gap-3">
              {/* Old Value */}
              <div className={`flex-1 ${isAdded ? 'opacity-50' : ''}`}>
                <div className="text-xs text-gray-500 mb-1">Previous</div>
                <div className={`text-sm ${isAdded ? 'text-gray-400' : 'text-red-600 line-through'}`}>
                  {formatValue(old_value)}
                </div>
              </div>

              {/* Arrow */}
              <div className="flex items-center pt-5">
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </div>

              {/* New Value */}
              <div className={`flex-1 ${isRemoved ? 'opacity-50' : ''}`}>
                <div className="text-xs text-gray-500 mb-1">New</div>
                <div className={`text-sm ${isRemoved ? 'text-gray-400' : 'text-green-600 font-medium'}`}>
                  {formatValue(new_value)}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * StatusChangeDiff Component
 *
 * Specialized view for status changes.
 */
export const StatusChangeDiff = ({ oldStatus, newStatus, reason }) => {
  return (
    <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="text-xs text-gray-500 mb-1">Previous Status</div>
          <div className="text-sm font-medium text-gray-700">{oldStatus || 'Unknown'}</div>
        </div>

        <ArrowRight className="w-5 h-5 text-blue-500" />

        <div className="flex-1">
          <div className="text-xs text-gray-500 mb-1">New Status</div>
          <div className="text-sm font-bold text-blue-700">{newStatus || 'Unknown'}</div>
        </div>
      </div>

      {reason && (
        <div className="mt-3 pt-3 border-t border-blue-200">
          <div className="text-xs text-gray-500 mb-1">Reason</div>
          <div className="text-sm text-gray-700">{reason}</div>
        </div>
      )}
    </div>
  );
};

export default DiffViewer;
